import routes from '@config/routes';
import instance from '@instance';
import getCookie from '@utils/get-cookie';

const USER_IP_COOKIE = 'user-ip';
const INVALID_IP_VALUES = new Set(['IP not found', 'Unknown IP']);

type RecordAuthenticatedUserIpArgs = {
  accessToken?: string | null;
  userId?: number | string | null;
};

export function appendValidIpAddress<T extends Record<string, any>>(
  payload: T,
  ipAddress = getUserIpAddress()
) {
  if (!ipAddress) {
    return payload;
  }

  return {
    ...payload,
    ipAddress,
  };
}

export function getUserIpAddress() {
  if (typeof document === 'undefined') return null;

  const ipAddress = getCookie(USER_IP_COOKIE)?.trim();

  if (!ipAddress || INVALID_IP_VALUES.has(ipAddress)) {
    return null;
  }

  return ipAddress;
}

function getTrackedUserIpKey(userId: number | string, ipAddress: string) {
  return `tracked-user-ip:${userId}:${ipAddress}`;
}

export async function recordAuthenticatedUserIp({
  accessToken,
  userId,
}: RecordAuthenticatedUserIpArgs) {
  if (
    typeof window === 'undefined' ||
    !accessToken ||
    userId === undefined ||
    userId === null
  ) {
    return;
  }

  const ipAddress = getUserIpAddress();

  if (!ipAddress) {
    return;
  }

  const trackedUserIpKey = getTrackedUserIpKey(userId, ipAddress);

  if (window.sessionStorage.getItem(trackedUserIpKey)) {
    return;
  }

  const response = await instance.server(routes.userIpAddress, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ ipAddress }),
  });

  if (!response.ok) {
    throw new Error('Failed to record user IP address');
  }

  window.sessionStorage.setItem(trackedUserIpKey, '1');
}
